import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfigStore } from '@renderer/stores/configState'
import {
  isIdrContext,
  languagesForCurrency,
  type BoothLanguage
} from '@renderer/utils/boothLanguages'

/**
 * Language UX is project-scoped: the currency stamped on the loaded project
 * tells us which audience the kiosk is talking to, and that decides which
 * languages the customer-facing screens offer (see
 * `@renderer/utils/boothLanguages` for the mapping).
 *
 *   - IDR (or an unstamped legacy project) — Indonesian audience: offer
 *     ID/EN and persist whichever the operator picked.
 *   - A Spanish-speaking Latin American currency (MXN, ARS, COP, …) —
 *     offer ES/EN and default to Spanish on entering the project.
 *   - Anything else — English only; the toggle is irrelevant noise, so it
 *     stays hidden and we force English regardless of what the operator
 *     left in `localStorage`.
 *
 * Three pieces:
 *   - `useProjectLanguages()` — the ordered list of languages this project
 *     offers (the project's own language first, English second). Render a
 *     picker from this instead of hardcoding a pair of buttons.
 *   - `useShouldShowLanguageSelector()` — read from any component that
 *     renders a language picker (the shared `LanguageSelector` plus the
 *     bahela / bahela-quick inline toggles). True when the project offers
 *     more than one language.
 *   - `useEnforceProjectLanguage()` — mount once in `ProjectRoute`. On load
 *     and whenever the project's `resolvedCurrency` changes, force
 *     `i18n.language` to the project's own language when the current one
 *     isn't on offer. Never writes to `localStorage` — the operator's
 *     persisted global preference stays intact so leaving the project (or
 *     opening an IDR one next) goes back to whatever they picked.
 */

export function useProjectLanguages(): BoothLanguage[] {
  const config = useConfigStore((s) => s.config)
  return languagesForCurrency(config?.resolvedCurrency)
}

export function useShouldShowLanguageSelector(): boolean {
  return useProjectLanguages().length > 1
}

export function useEnforceProjectLanguage(): void {
  const { i18n } = useTranslation()
  const resolvedCurrency = useConfigStore((s) => s.config?.resolvedCurrency)

  useEffect(() => {
    // Only act once we have a real project loaded (resolvedCurrency
    // defined). Pre-project state should still respect whatever the
    // operator picked globally — we only override inside the project.
    if (!resolvedCurrency) return
    if (isIdrContext(resolvedCurrency)) return
    // Force the project's OWN language, not merely "one it offers". i18next
    // boots at `en` and the operator's stored preference is usually `en`
    // too, so accepting any offered language would leave a Mexican booth
    // greeting Spanish-speaking guests in English until someone tapped ES.
    // Non-IDR projects have always been re-stamped on entry like this; the
    // only change is that the stamp is no longer hardcoded to English.
    const [projectLanguage] = languagesForCurrency(resolvedCurrency)
    if (i18n.language === projectLanguage) return
    // Don't persist: when the operator backs out of the project (or
    // opens an IDR one next), App.tsx's mount-time localStorage check
    // restores their original preference. A language they pick inside the
    // project also sticks, because this only re-runs when the project's
    // currency changes.
    void i18n.changeLanguage(projectLanguage)
  }, [resolvedCurrency, i18n])
}
